/**
 * event-icon-cache.ts
 *
 * Persists event icon assignments in localStorage so that they survive
 * page reloads even when the backend does not store the `icon` field.
 *
 * Storage key: "dg_event_icons"
 * Value: JSON object mapping eventId (string) → iconName (string)
 */

const STORAGE_KEY = 'dg_event_icons';

function getCache(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveCache(cache: Record<string, string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // ignore quota errors
  }
}

/** Persist an icon name for a specific event id */
export function setEventIcon(eventId: string | number, iconName: string): void {
  const cache = getCache();
  if (iconName) {
    cache[String(eventId)] = iconName;
  } else {
    delete cache[String(eventId)];
  }
  saveCache(cache);
}

/** Retrieve the stored icon name for a specific event id (or empty string) */
export function getEventIcon(eventId: string | number): string {
  const cache = getCache();
  return cache[String(eventId)] || '';
}

/** Remove an event's icon from the cache (e.g. when the event is deleted) */
export function removeEventIcon(eventId: string | number): void {
  const cache = getCache();
  delete cache[String(eventId)];
  saveCache(cache);
}

/**
 * Given a list of events that came from the API (possibly missing `icon`),
 * rehydrate the icon field from the local cache when it's absent.
 * Also handles recurring-event instance IDs like "123_1736000000" by
 * falling back to the base event ID "123".
 */
export function rehydrateIcons<T extends { id: string | number; icon?: string }>(events: T[]): T[] {
  return events.map((ev) => {
    if (!ev.icon) {
      // First try exact id match
      let cached = getEventIcon(ev.id);
      if (!cached) {
        // Try base id (for recurring instances like "123_timestamp")
        const baseId = String(ev.id).split('_')[0];
        if (baseId !== String(ev.id)) {
          cached = getEventIcon(baseId);
        }
      }
      if (cached) {
        return { ...ev, icon: cached };
      }
    }
    return ev;
  });
}
